import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import './PDFViewer.scss';
import { formatFileSize, getScreenDimensions } from "../../utils/helper";
import Loader from "../Loader/Loader";
import images from "../../assets/images";
import { assignment } from "../../pages/Assignments/Assignments";
import { isPlatform } from "@ionic/react";
import { ElectronFileDetail, CordovaFileDetail } from "../../utils/interface";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Tooltip } from "@mui/material";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { MYLIBRARY_TEXTS } from "../../utils/constants";
/*
unpkg is a fast, global content delivery network for everything on npm. 
Use it to quickly and easily load any file from any package using a URL 
*/
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Config {
  fullLoading: boolean;
  numPages: number | null;
  scale: number;
  pageNumber: number;
  page: number;
  minScale: number;
  maxScale: number;
  scalePoint: number;
  width: number;
}

interface material {
  filename: string;
  filepath: string;
  filesize: number;
  isclassroom: boolean;
  coverimagepath?: string;
  authorname?: string;
};

interface PDFViewerProps {
  file: string;
  pdfFileData: material | assignment | undefined | CordovaFileDetail | ElectronFileDetail| null;
  onClose: () => void;
  type: string;
}

interface LoadEvent {
  numPages: number;
}

const iconStyle = {
  height: 28,
  width: 28
};

const CreatePages = (numPages: number | null, scale: number, page: number, width: number, onPageRender: () => void) => {
  const pages: JSX.Element[] = [];
  if (numPages === null) {
    return pages;
  }

  for (let i = page; i <= numPages; i++) {
    if (i > numPages) {
      break;
    }
    pages.push(
      <Page
        key={`page-${i}`}
        renderMode="canvas"
        loading={<Loader />}
        pageNumber={i}
        width={scale}
        // scale={scale}
        renderTextLayer={true}
        renderAnnotationLayer={true}
        onRenderSuccess={onPageRender}
      />
    );
  }

  return pages;
};

const PDFViewer: React.FC<PDFViewerProps> = ({ file, pdfFileData, onClose, type }) => {
  const { guideDefaultImage } = images;
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  console.log('file inside the pdf viewer=========>',file);
  const fileOrBase64 = file.toLowerCase().endsWith(".pdf")
    ? file
    : `data:application/pdf;base64,${file}`;
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true);
  const [pagesRendered, setPagesRendered] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const { isMobileScreen } = getScreenDimensions();
  const [config, setConfig] = useState<Config>({
    fullLoading: false,
    numPages: null,
    scale: window.innerWidth * 0.5,
    pageNumber: 10,
    page: 1,
    minScale: 250,
    maxScale: window.innerWidth * 1.5,
    scalePoint: 0.2,
    width: isMobileScreen ? window.innerWidth - 50 : window.innerWidth * 0.5
  });

  const zoomIn = () => {
    setConfig((prevConfig) => {
      const newScalePoint = prevConfig.scale * 0.1;
      const newScale = prevConfig.scale + newScalePoint;

      return {
        ...prevConfig,
        scale: newScale,
        scalePoint: newScalePoint
      };
    });
  };

  const zoomOut = () => {
    setConfig((prevConfig) => {
      const newScalePoint = prevConfig.scale * 0.1;
      const newScale = prevConfig.scale - newScalePoint;
  
      return {
        ...prevConfig,
        scale: newScale,
        scalePoint: newScalePoint
      };
    });
  };

  const onDocumentLoadSuccess = ({ numPages }: LoadEvent) => {
    setConfig({ ...config, numPages, pageNumber: numPages, fullLoading: true });
    setPagesRendered(0); // Reset pages rendered count
    setIsDocumentLoading(true); // Continue loading until all pages are rendered
    setError(null);
  };

  // Called when each individual page is rendered
  const onPageRenderSuccess = () => {
    setPagesRendered((prevRendered) => {
      const newRendered = prevRendered + 1;
      if (newRendered === config.numPages) {
        setIsDocumentLoading(false); // Stop loading when all pages are rendered
      }
      return newRendered;
    });
  };

  const onDocumentLoadError = (error: Error) => {
    setError(error.message); // Set error message
    setIsDocumentLoading(false); // Stop loading in case of error
  };

  // useEffect(() => {
  //   const handleResize = () => {
  //     setConfig(prevConfig => ({
  //       ...prevConfig,
  //       width: isMobileScreen ? window.innerWidth - 50 : window.innerWidth * 0.5
  //     }));
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => {
  //     window.removeEventListener("resize", handleResize);
  //   };
  // }, []);


  const textRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  // Function to check if text is truncated
  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis(); // Check on mount
    window.addEventListener("resize", checkEllipsis); // Recheck on resize

    return () => window.removeEventListener("resize", checkEllipsis); // Cleanup on unmount
  }, [checkEllipsis, pdfFileData?.filename]);

  return (
    <div className={type === MYLIBRARY_TEXTS?.EXAMINATION_GUIDE_TEXT ? "pdfOuterGuideContainer" :type === "Assignment" ? "pdfOuterAssignmentContainer" : type === 'chat' ? "chatPdfContainer" :"pdfContainer"}>
      <div className={type === MYLIBRARY_TEXTS?.EXAMINATION_GUIDE_TEXT ? "pdfGuideContainer" : error ? "pdfInnerContainer" : "pdfInner"}>
        {(type === MYLIBRARY_TEXTS?.EXAMINATION_GUIDE_TEXT) && (
          <div className="PdfHeaderCtn">
            <img src={pdfFileData?.coverimagepath ? baseUrl + pdfFileData?.coverimagepath : guideDefaultImage} alt="guide-img" className="guideImage" />
            <div className="PdfHeaderGuide">
              <div className="guideTitle">{pdfFileData?.filename}</div>
              <div className="author">{pdfFileData?.authorname}</div>
            </div>
          </div>
        )}
        {!(["Questions bank", "chat", "system_messages"].includes(type)) && (
            <div className="PdfHeader">
              <Tooltip title={isEllipsisActive ? pdfFileData?.filename : ""}>
                <div ref={textRef} className="fileName">
                  {pdfFileData?.filename}
                </div>
                </Tooltip>
              <div className="fileSize">Size: {pdfFileData?.filesize ? formatFileSize(pdfFileData?.filesize) : '0 Mb'}</div>
            </div>
        )}
        {isDocumentLoading && <Loader bottom="200px" />}
        <div style={{ display: isDocumentLoading ? "none" : undefined, position: "relative", flex: 1 }} className={(type === MYLIBRARY_TEXTS?.EXAMINATION_GUIDE_TEXT && error) ? "guideContent errorContent" : type === MYLIBRARY_TEXTS?.EXAMINATION_GUIDE_TEXT ? "guideContent" : "content"}>
            {error ? (
              <div className="errorContainer">
                <p className="errorMessage">{error}</p>
              </div>
            ) : (
              <Document
                file={(isPlatform('android')||(window?.electron && window.electron.isElectron())) ? file : fileOrBase64}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<Loader bottom="50px" />}
                className={"gridItem"}
                externalLinkTarget="_blank"
              >
                {CreatePages(config.numPages, config.scale, config.page, config.width, onPageRenderSuccess)}
              </Document>
            )}
        </div>
        {(!isDocumentLoading) && (
          <div className="zoomIconsContainer">
            <button className="zoomOut" onClick={zoomOut} style={{ pointerEvents: config.scale <= config.minScale ? "none" : "unset" }} disabled={config.scale <= config.minScale}>
              <ZoomOutIcon sx={iconStyle} className="zoomOutIcon" />
            </button>
            <div className="divider" />
            <button className="zoomIn" onClick={zoomIn} style={{ pointerEvents: config.scale >= config.maxScale ? "none" : "unset" }} disabled={config.scale >= config.maxScale}>
              <ZoomInIcon sx={iconStyle} className="zoomInIcon" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
